import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, filter, map, Observable, of, switchMap } from 'rxjs';
import { OrderApi } from 'src/app/module/order/api/order.api';
import {
  AddComparableParams,
  AddCompValidationStatus,
  AddNewCustomComparableParams,
  SelectedComparable,
  ValuationType,
} from 'src/app/module/order/interface/order.interface';
import { ApproachFactoryService } from 'src/app/module/order/service/approach-factory.service';
import { OrderService } from 'src/app/module/order/service/order.service';
import { ApproachFactoryState } from 'src/app/module/order/state/approach-factory.state';
import { OrderState } from 'src/app/module/order/state/order.state';
import { ConfirmPropertyParam } from 'src/app/module/short-term-rental/interface/short-term-rental.interface';
import { ShortTermRentalService } from 'src/app/module/short-term-rental/service/short-term-rental.service';
import { SyncScope, SyncUpdateScope, SyncUpdateSet } from 'src/app/module/sync/interface/sync.interface';
import { SyncService } from 'src/app/module/sync/service/sync.service';
import { SelectedSourceDataPipe } from 'src/app/shared/pipes/selected-source-data.pipe';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { ScenarioState } from '../../scenario/state/scenario.state';
import { ValidationRuleEngineService } from '../../validation/service/validation-rule-engine.service';
import { ComparableService } from './comparable.service';

@Injectable({
  providedIn: 'root',
})
export class AddComparableService {
  constructor(
    private approachFactoryService: ApproachFactoryService,
    private approachFactoryState: ApproachFactoryState,
    private comparableService: ComparableService,
    private orderApi: OrderApi,
    private orderService: OrderService,
    private orderState: OrderState,
    private scenarioState: ScenarioState,
    private selectedSourceDataPipe: SelectedSourceDataPipe,
    private shortTermRentalService: ShortTermRentalService,
    private snackBarService: SnackBarService,
    private syncService: SyncService,
    private validationRuleEngineService: ValidationRuleEngineService
  ) {}

  /** Adding Specific comparable to different approach comparable list */
  addingComparableFromAddComp(reqBody: AddComparableParams): Observable<boolean> {
    return this.addingComparable(reqBody);
  }

  /** Adding Custom comparable to different valuation model comparable list */
  addingCustomComparableFromAddComp(reqParams: AddNewCustomComparableParams): Observable<boolean> {
    return this.addingCustomComparable(reqParams);
  }

  /**Adding specific/custom comp to selected Comparable list of respective valuation model when listed comp is already in the comparable list */
  selectingComparableFromAddComp(validationResult: AddCompValidationStatus): Observable<boolean> {
    return this.selectingComparable(validationResult);
  }

  /**Match a property listed in   respective valuation model*/
  confirmProperty(reqParams: ConfirmPropertyParam, valuationType: ValuationType): Observable<boolean> {
    if (valuationType == ValuationType.SHORT_TERM_RENTAL) {
      return this.shortTermRentalService.confirmProperty(reqParams);
    } else return of(true);
  }

  addingComparable(reqBody: AddComparableParams): Observable<boolean> {
    return this.orderApi.addComparable(reqBody).pipe(
      switchMap((res) => {
        if (res?.propertyId && this.orderState.activeValuationType) {
          return this.comparableService
            .fetchSelectedComparables(this.scenarioState.activeScenario!.id, this.orderState.activeValuationType)
            .pipe(
              map(() => {
                const selectedCompList = this.approachFactoryState.getOrderValue('selectedComparableList') ?? [];

                const newComp = Object.values(this.approachFactoryState.selectedComparableMap).find(
                  (comp) => comp.id == res.propertyId
                );

                if (newComp?.id)
                  selectedCompList.push({
                    id: newComp.id,
                    ListingId: this.selectedSourceDataPipe.transform(newComp.ListingId),
                  });

                const activeScenario = structuredClone(this.scenarioState.activeScenario);

                activeScenario![this.approachFactoryState.activeApproachKey]!.selectedComparableList = [
                  ...selectedCompList,
                ];

                this.syncService.syncLocally(SyncScope.SCENARIO, activeScenario);

                this.afterProcessingComparable(newComp?.id);
                this.snackBarService.open('success', 'Comparable added successfully');
                return true;
              }),
              catchError(() => {
                return of(false);
              })
            );
        } else return of(false);
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  addingCustomComparable(reqParams: AddNewCustomComparableParams) {
    return this.orderApi.addCustomComparable(reqParams).pipe(
      switchMap((res) => {
        if (res?.listingId && res?.propertyId && this.orderState.activeValuationType) {
          return this.comparableService
            .fetchSelectedComparables(this.scenarioState.activeScenario!.id, this.orderState.activeValuationType)
            .pipe(
              map(() => {
                const selectedCompList = this.approachFactoryState.getOrderValue('selectedComparableList') ?? [];

                selectedCompList.push({
                  id: res.propertyId,
                  ListingId: res.listingId,
                });

                const activeScenario = structuredClone(this.scenarioState.activeScenario);

                activeScenario![this.approachFactoryState.activeApproachKey]!.selectedComparableList = [
                  ...selectedCompList,
                ];

                this.syncService.syncLocally(SyncScope.SCENARIO, activeScenario);

                this.afterProcessingComparable(res.propertyId);
                this.snackBarService.open('success', 'Comparable added successfully');
                return true;
              }),
              catchError(() => {
                return of(false);
              })
            );
        } else {
          return of(false);
        }
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  removeComparable(validationResult: AddCompValidationStatus): Observable<boolean> {
    const selectedCompList: SelectedComparable[] =
      this.approachFactoryState.getOrderValue('selectedComparableList') ?? [];

    const selectedCompIndex = selectedCompList.findIndex(
      (comp) => comp.ListingId == validationResult?.propertyInfo?.listingId
    );

    if (selectedCompIndex > -1) selectedCompList.splice(selectedCompIndex, 1);

    /**Calling update order directly instead of syncUpdate to update state instantly */
    return this.orderApi
      .removeComparable(validationResult?.propertyInfo?.propertyId ?? '', this.orderState.activeValuationType!)
      .pipe(
        switchMap((res) => {
          if (res && this.orderState.activeValuationType) {
            const activeScenario = structuredClone(this.scenarioState.activeScenario);

            activeScenario![this.approachFactoryState.activeApproachKey]!.selectedComparableList = [
              ...selectedCompList,
            ];

            this.syncService.syncLocally(SyncScope.SCENARIO, activeScenario);

            return this.comparableService
              .fetchSelectedComparables(this.scenarioState.activeScenario!.id, this.orderState.activeValuationType)
              .pipe(
                map(() => {
                  this.afterProcessingComparable();
                  this.snackBarService.open('success', 'Comparable removed successfully');
                  return true;
                }),
                catchError(() => {
                  return of(false);
                })
              );
          } else {
            return of(false);
          }
        }),
        catchError(() => {
          return of(false);
        })
      );
  }

  selectingComparable(validationResult: AddCompValidationStatus): Observable<boolean> {
    const selectedCompList: SelectedComparable[] =
      this.approachFactoryState.getOrderValue('selectedComparableList') ?? [];

    if (validationResult?.propertyInfo?.listingId && validationResult?.propertyInfo?.propertyId)
      selectedCompList.push({
        id: validationResult?.propertyInfo?.propertyId,
        ListingId: validationResult?.propertyInfo?.listingId,
      });

    const updateSet: SyncUpdateSet[] = [];
    updateSet.push({
      scope: SyncUpdateScope.SCENARIO,
      id: this.scenarioState.activeScenario?.id,
      jsonPath:
        this.orderState.activeValuationType == ValuationType.SALES
          ? 'salesComparison.selectedComparableList'
          : this.orderState.activeValuationType == ValuationType.SHORT_TERM_RENTAL
            ? 'shortTermRental.selectedComparableList'
            : this.orderState.activeValuationType == ValuationType.LONG_TERM_RENTAL
              ? 'longTermRental.selectedComparableList'
              : '',
      value: selectedCompList,
    });

    /**Calling update order directly instead of syncUpdate to update state instantly */
    return this.orderService.updateOrder(updateSet).pipe(
      filter((res) => res),
      switchMap(() => {
        if (this.orderState.activeValuationType) {
          const activeScenario = structuredClone(this.scenarioState.activeScenario);

          activeScenario![this.approachFactoryState.activeApproachKey]!.selectedComparableList = [...selectedCompList];

          this.syncService.syncLocally(SyncScope.SCENARIO, activeScenario);

          return this.comparableService
            .fetchSelectedComparables(this.scenarioState.activeScenario!.id, this.orderState.activeValuationType)
            .pipe(
              map(() => {
                this.afterProcessingComparable(validationResult?.propertyInfo?.propertyId);
                this.snackBarService.open('success', 'Comparable selected successfully');
                return true;
              }),
              catchError(() => {
                return of(false);
              })
            );
        } else {
          return of(false);
        }
      }),
      catchError(({ error }: HttpErrorResponse) => {
        this.snackBarService.open('error', error.message || 'Failed to add comparable');
        return of(false);
      })
    );
  }

  afterProcessingComparable(addedPropertyId?: string) {
    this.approachFactoryService.recalculateAndUpdateProperties();

    // Reconcile aggregates; a comp added after an audit also gets its per-field FE rules.
    this.validationRuleEngineService.reconcileAfterComparableChange(addedPropertyId);

    this.approachFactoryState.secondarySearchFilter = {
      ...this.approachFactoryState.activeApproachSetting?.secondarySearchFilter,
      fetchSelectedComparables: true,
    };
  }
}
